import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Upload, Loader2, ShieldAlert, RotateCcw, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../../context/AppContext';
import UpgradeProModal from '../../components/UpgradeProModal';
import api from '../../api';

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function SymptomCheck() {
  const nav = useNavigate();
  const { selectedCat, user } = useApp();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setB64(await fileToBase64(f));
    setResult(null);
  };

  const analyze = async () => {
    if (!b64) { toast.error('Please add a photo first'); return; }
    setLoading(true);
    try {
      const r = await api.post('/ai/symptom', { image_base64: b64, note, cat_name: selectedCat?.name, cat_breed: selectedCat?.breed });
      setResult(r.data.result);
      if (r.data.remaining !== null && r.data.remaining <= 0 && !user?.is_pro) setShowUpgrade(true);
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowUpgrade(true);
      } else {
        toast.error('Could not analyze the photo. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const reset = () => { setPreview(null); setB64(null); setNote(''); setResult(null); };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 border-b border-border bg-background/95 px-4 py-3 backdrop-blur">
        <h1 className="text-base font-extrabold">Symptom photo check</h1>
        <p className="text-xs text-muted-foreground">Upload a photo and get instant AI guidance</p>
      </header>

      <div className="px-4 py-4">
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />

        {!preview ? (
          <button onClick={() => fileRef.current?.click()} className="flex w-full flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-border bg-secondary/40 py-14 transition hover:border-[hsl(var(--coral))]">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent"><Camera className="h-8 w-8 text-[hsl(var(--coral))]" /></div>
            <p className="font-bold">Tap to add a photo</p>
            <p className="text-xs text-muted-foreground">Eyes, skin, ears, teeth, wounds…</p>
          </button>
        ) : (
          <div className="animate-fade-up overflow-hidden rounded-3xl">
            <img src={preview} alt="symptom" className="h-56 w-full object-cover" />
          </div>
        )}

        {preview && (
          <>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Describe what you’re seeing (optional)…" rows={3} className="mt-4 w-full resize-none rounded-2xl border border-input bg-white p-4 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
            {!result && (
              <button onClick={analyze} disabled={loading} className="coral-btn mt-4 flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-bold text-white disabled:opacity-70">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" />Analyzing…</> : <><Upload className="h-5 w-5" />Analyze photo</>}
              </button>
            )}
          </>
        )}

        {result && (
          <div className="mt-5 animate-fade-up space-y-4">
            {result.emergency && (
              <div className="flex items-start gap-2 rounded-2xl border border-destructive/40 bg-destructive/10 p-3">
                <TriangleAlert className="h-5 w-5 shrink-0 text-destructive" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-destructive">This could be an emergency</p>
                  <p className="text-xs text-muted-foreground">Please contact an emergency vet right away.</p>
                  <button onClick={() => nav('/app/vets')} className="mt-2 rounded-full bg-destructive px-3 py-1.5 text-xs font-bold text-white">Find emergency vet</button>
                </div>
              </div>
            )}
            <div className="rounded-2xl bg-white p-5 card-soft">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-extrabold">{result.title}</h2>
                <span className="rounded-full bg-accent px-3 py-1 text-xs font-bold text-[hsl(var(--coral))]">{result.confidence}</span>
              </div>
              <p className="mt-1 text-sm font-semibold text-muted-foreground">Possible causes</p>
              <ul className="mt-2 space-y-1.5 text-sm">
                {(result.causes || []).map((c, i) => <li key={i} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--coral))]" />{c}</li>)}
              </ul>
              <p className="mt-4 rounded-xl bg-secondary/60 p-3 text-sm text-foreground">{result.advice}</p>
            </div>
            <div className="flex items-start gap-2 rounded-xl bg-accent/60 p-3 text-xs text-accent-foreground">
              <ShieldAlert className="h-4 w-4 shrink-0" /> This is AI guidance, not a diagnosis. For anything urgent, contact a vet.
            </div>
            <button onClick={reset} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-border bg-white py-3 text-sm font-bold"><RotateCcw className="h-4 w-4" />Check another photo</button>
          </div>
        )}
      </div>
      <UpgradeProModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />
    </div>
  );
}
